import { useEffect, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';

/**
 * Asks before a private skill is shared to a workspace, and whether the author keeps their own
 * copy. The copy is kept unless they clear the box; without it the private skill is removed once
 * the workspace has its copy.
 */
export function ShareToWorkspaceDialog({
  open,
  onOpenChange,
  trigger,
  workspaceName,
  onShare,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: string;
  workspaceName: string | null;
  onShare: (keepCopy: boolean) => void;
}): JSX.Element {
  const [keepCopy, setKeepCopy] = useState(true);
  /** Each opening starts ticked: a share cancelled after clearing the box must not carry it over. */
  useEffect(() => {
    if (open) setKeepCopy(true);
  }, [open]);
  const place = workspaceName ?? 'the workspace';
  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Share /${trigger} to ${place}?`}
      description={`Everyone in ${place} can use it, and its editors can change it.`}
    >
      <div className="mt-3 flex flex-col gap-4">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={keepCopy}
            onCheckedChange={(checked) => setKeepCopy(checked === true)}
          />
          Keep a copy in my personal skills
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => {
              onOpenChange(false);
              onShare(keepCopy);
            }}
          >
            Share
          </Button>
        </div>
      </div>
    </AppDialog>
  );
}
